// apps/web/src/features/cargo/components/CargoActiveFilters.tsx
//
// Always-visible active-filter chips for `/cargo` (#1887). Surfaces every
// applied filter axis — including the IMPLICIT logist default-date window —
// as a small chip with a clear (×) control, so a filtered view is never
// invisible behind the collapsed filters panel. Rendered only when ≥1 chip
// applies. No shared `Chip` primitive exists in apps/web/src/ui, so chips are
// built feature-locally with CSS tokens per apps/web/CLAUDE.md.

import type { CSSProperties } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  CARGO_CARRIER_PAYMENT_STATUS_LABELS,
  CARGO_CLIENT_PAYMENT_STATUS_LABELS,
} from '@magimp/shared';

import { Icon } from '../../../ui/index.js';
import type { CargoManagerOption } from './CargoFiltersPanel.js';
import { isCargoRangeBoundActive } from '../cargoFiltersStorage.js';
import type { UseCargoFiltersResult } from '../useCargoFilters.js';

export interface CargoActiveFilterChip {
  key: string;
  label: string;
  value: string;
  onClear: () => void;
}

type CargoFilterTranslator = TFunction<readonly ['cargo']>;

function rangeValue(from: string, to: string, t: CargoFilterTranslator): string | null {
  const activeFrom = isCargoRangeBoundActive(from) ? from : '';
  const activeTo = isCargoRangeBoundActive(to) ? to : '';
  if (activeFrom && activeTo) return `${activeFrom} — ${activeTo}`;
  if (activeFrom) return t('cargo:activeFilters.from', { value: activeFrom });
  if (activeTo) return t('cargo:activeFilters.to', { value: activeTo });
  return null;
}

export function buildCargoActiveFilterChips(
  filters: UseCargoFiltersResult,
  managerOptions: readonly CargoManagerOption[],
  t: CargoFilterTranslator,
): CargoActiveFilterChip[] {
  const chips: CargoActiveFilterChip[] = [];

  const q = filters.q.trim();
  if (q) {
    chips.push({
      key: 'q',
      label: t('cargo:activeFilters.search'),
      value: q,
      onClear: () => filters.setQ(''),
    });
  }

  if (filters.lifecycle) {
    chips.push({
      key: 'lifecycle',
      label: t('cargo:filters.status'),
      value: t(`cargo:lifecycle.${filters.lifecycle}`),
      onClear: () => filters.setLifecycle(null),
    });
  }

  if (filters.responsibleManagerId) {
    const name = managerOptions.find((m) => m.id === filters.responsibleManagerId)?.name;
    chips.push({
      key: 'manager',
      label: t('cargo:filters.manager'),
      value: name ?? filters.responsibleManagerId,
      onClear: () => filters.setResponsibleManagerId(''),
    });
  }

  if (filters.carrier) {
    chips.push({
      key: 'carrier',
      label: t('cargo:filters.carrier'),
      value: filters.carrier,
      onClear: () => filters.setCarrier(null),
    });
  }

  if (filters.pathFormat) {
    chips.push({
      key: 'pathFormat',
      label: t('cargo:filters.pathFormat'),
      value: filters.pathFormat,
      onClear: () => filters.setPathFormat(null),
    });
  }

  if (filters.clientPaymentStatus) {
    chips.push({
      key: 'clientPaymentStatus',
      label: t('cargo:filters.clientPaymentStatus'),
      value: CARGO_CLIENT_PAYMENT_STATUS_LABELS[filters.clientPaymentStatus],
      onClear: () => filters.setClientPaymentStatus(null),
    });
  }

  if (filters.carrierPaymentStatus) {
    chips.push({
      key: 'carrierPaymentStatus',
      label: t('cargo:filters.carrierPaymentStatus'),
      value: CARGO_CARRIER_PAYMENT_STATUS_LABELS[filters.carrierPaymentStatus],
      onClear: () => filters.setCarrierPaymentStatus(null),
    });
  }

  if (filters.overdue) {
    chips.push({
      key: 'overdue',
      label: t('cargo:filters.overdue'),
      value: t(`cargo:overdue.${filters.overdue}`),
      onClear: () => filters.setOverdue(null),
    });
  }

  if (filters.hasRejectedDocument === true) {
    chips.push({
      key: 'hasRejectedDocument',
      label: t('cargo:filters.hasRejectedDocument'),
      value: t('cargo:filters.hasRejectedDocumentHint'),
      onClear: () => filters.setHasRejectedDocument(false),
    });
  }

  const weightRange = rangeValue(filters.weightFrom, filters.weightTo, t);
  if (weightRange !== null) {
    chips.push({
      key: 'weightRange',
      label: t('cargo:filters.weightRange'),
      value: weightRange,
      onClear: () => {
        filters.setRangeBound('weightFrom', '');
        filters.setRangeBound('weightTo', '');
      },
    });
  }

  const volumeRange = rangeValue(filters.volumeFrom, filters.volumeTo, t);
  if (volumeRange !== null) {
    chips.push({
      key: 'volumeRange',
      label: t('cargo:filters.volumeRange'),
      value: volumeRange,
      onClear: () => {
        filters.setRangeBound('volumeFrom', '');
        filters.setRangeBound('volumeTo', '');
      },
    });
  }

  const totalCostRange = rangeValue(filters.totalCostFrom, filters.totalCostTo, t);
  if (totalCostRange !== null) {
    chips.push({
      key: 'totalCostRange',
      label: t('cargo:filters.totalCostRange'),
      value: totalCostRange,
      onClear: () => {
        filters.setRangeBound('totalCostFrom', '');
        filters.setRangeBound('totalCostTo', '');
      },
    });
  }

  const hasExplicitDates = filters.dateFrom.length > 0 || filters.dateTo.length > 0;
  if (hasExplicitDates) {
    const range = [filters.dateFrom, filters.dateTo].filter(Boolean).join(' — ');
    chips.push({
      key: 'dateRange',
      label: t(`cargo:dateTypes.${filters.dateType}`),
      value: t('cargo:activeFilters.dateRange', { range }),
      onClear: () => {
        filters.setDateFrom('');
        filters.setDateTo('');
      },
    });
  } else if (filters.usingDefaultDateWindow && filters.defaultDateWindow) {
    // Clearing the implicit window stamps `datesTouched` (via the date setters)
    // so the default never reappears — no new query param (D-5).
    const { from, to } = filters.defaultDateWindow;
    chips.push({
      key: 'defaultDateWindow',
      label: t(`cargo:dateTypes.${filters.dateType}`),
      value: t('cargo:activeFilters.dateRange', { range: `${from} — ${to}` }),
      onClear: () => {
        filters.setDateFrom('');
        filters.setDateTo('');
      },
    });
  }

  return chips;
}

export function CargoActiveFilters({
  filters,
  managerOptions,
}: {
  filters: UseCargoFiltersResult;
  managerOptions: readonly CargoManagerOption[];
}) {
  const { t } = useTranslation(['cargo']);
  const chips = buildCargoActiveFilterChips(filters, managerOptions, t);

  if (chips.length === 0) return null;

  return (
    <div data-testid="cargo-active-filters" style={rowStyle}>
      {chips.map((chip) => (
        <span key={chip.key} data-testid={`cargo-active-filter-${chip.key}`} style={chipStyle}>
          <span style={{ color: 'var(--fg-3)' }}>{chip.label}:</span>
          <span
            title={chip.value}
            style={{
              color: 'var(--fg-1)',
              fontWeight: 500,
              maxWidth: 220,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {chip.value}
          </span>
          <button
            type="button"
            aria-label={t('cargo:activeFilters.clear', { label: chip.label })}
            onClick={chip.onClear}
            style={clearButtonStyle}
          >
            <Icon name="x" size={12} />
          </button>
        </span>
      ))}
    </div>
  );
}

const rowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  padding: '10px 24px',
  borderBottom: '1px solid var(--border-1)',
  background: 'var(--bg-1)',
  flex: 'none',
};

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  height: 24,
  padding: '0 4px 0 9px',
  borderRadius: 'var(--r-1)',
  fontSize: 'var(--fz-12)',
  border: '1px solid var(--border-1)',
  background: 'var(--bg-0)',
  whiteSpace: 'nowrap',
};

const clearButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  padding: 0,
  border: 0,
  borderRadius: 'var(--r-1)',
  background: 'transparent',
  color: 'var(--fg-3)',
  cursor: 'pointer',
};
